import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  Container, Paper, Typography, TextField, Button, 
  FormControl, InputLabel, MenuItem, Select, Grid,
  CircularProgress, Alert, Box, Divider, 
  Tooltip, Stack, Card, CardContent, useTheme,
  alpha, Stepper, Step, StepLabel, StepContent,
  LinearProgress, Chip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import BusinessIcon from '@mui/icons-material/Business';
import { generateSpecification, generateSpecificationEnhanced, getProjects, getUserProjects } from '../utils/api';
import { getProjectDocuments } from '../utils/documentProjectService';
import { useAuth } from '../contexts/AuthContext';

// Document sections are now handled by the backend

const GenerateSpecPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: urlProjectId } = useParams();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    project_type: 'SAP S/4HANA Cloud, Private Cloud Implementation',
    author_name: currentUser?.full_name || '',
    date: new Date().toISOString().split('T')[0],
    project_name: '',
    client_name: '', 
    custom_prompt: '',
    project_id: '' // This will be populated from URL query param or localStorage
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDocuments, setProjectDocuments] = useState([]);
  const [sowAvailable, setSowAvailable] = useState(false);
  const [useEnhancedGeneration, setUseEnhancedGeneration] = useState(true);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If project_id is changed, update selectedProject and check for documents
    if (name === 'project_id' && value) {
      const project = projects.find(p => p.id.toString() === value.toString());
      setSelectedProject(project);
      // Check for project documents and SOW availability
      checkProjectDocuments(value);
    }
  };
  
  // Check for project documents and SOW availability
  const checkProjectDocuments = async (projectId) => {
    if (!projectId) {
      setProjectDocuments([]);
      setSowAvailable(false);
      return;
    }
    
    try {
      const response = await getProjectDocuments(projectId);
      if (response.success && response.documents) {
        setProjectDocuments(response.documents);
        // Check if there's a SOW document
        const hasSow = response.documents.some(doc => 
          doc.document_type === 'SOW' || 
          (doc.title && doc.title.toUpperCase().includes('SOW')) ||
          (doc.title && doc.title.toUpperCase().includes('STATEMENT OF WORK'))
        );
        setSowAvailable(hasSow);
        
        // If SOW is available, default to enhanced generation
        if (hasSow) {
          setUseEnhancedGeneration(true);
        }
      } else {
        setProjectDocuments([]);
        setSowAvailable(false);
      }
    } catch (error) {
      console.error('Error checking project documents:', error);
      setProjectDocuments([]);
      setSowAvailable(false);
    }
  };
  
  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        
        // For regular users, fetch only their assigned projects
        let response;
        if (currentUser.role === 'admin') {
          response = await getProjects();
        } else {
          response = await getUserProjects(currentUser.id);
        }
        
        if (response.success) {
          setProjects(response.projects || []);
          
          // If there's only one project and no project_id is set, automatically select it
          if ((response.projects || []).length === 1 && !formData.project_id) {
            const projectId = response.projects[0].id.toString();
            setFormData(prev => ({ ...prev, project_id: projectId }));
            setSelectedProject(response.projects[0]);
          }
        } else {
          console.error('Error fetching projects:', response.message);
          setProjects([]);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };

    if (currentUser) {
      fetchProjects();
    }
    
    // Use URL projectId (from realize phase layout) if available,
    // otherwise fall back to query param or localStorage
    if (urlProjectId) {
      setFormData(prev => ({ ...prev, project_id: urlProjectId }));
    } else {
      const params = new URLSearchParams(location.search);
      const projectId = params.get('project');
      if (projectId) {
        setFormData(prev => ({ ...prev, project_id: projectId }));
        localStorage.setItem('selectedProject', projectId);
      } else {
        const savedProject = localStorage.getItem('selectedProject');
        if (savedProject) {
          try {
            const parsed = JSON.parse(savedProject);
            const id = parsed?.id ? parsed.id.toString() : savedProject;
            setFormData(prev => ({ ...prev, project_id: id }));
          } catch {
            setFormData(prev => ({ ...prev, project_id: savedProject }));
          }
        }
      }
    }
  }, [location, currentUser, urlProjectId]);

  // Pre-fill project name and client name when project is selected
  useEffect(() => {
    if (formData.project_id && projects.length > 0) {
      const selectedProject = projects.find(p => p.id.toString() === formData.project_id.toString());
      if (selectedProject) {
        setFormData(prev => ({
          ...prev,
          project_name: selectedProject.name,
          client_name: prev.client_name || selectedProject.name || '',
          // Pre-fill project_type from project if set in overview
          project_type: selectedProject.project_type || prev.project_type
        }));
        setSelectedProject(selectedProject);
      }
    }
  }, [formData.project_id, projects]);

  // Loading progress simulation
  React.useEffect(() => {
    let timer;
    if (loading) {
      timer = setInterval(() => {
        setLoadingProgress((prevProgress) => {
          // Slowly increase up to 90% (to show we're still working)
          if (prevProgress >= 90) {
            clearInterval(timer);
            return 90;
          }
          return prevProgress + 1;
        });
      }, 1000); // Update every second
    } else if (loadingProgress > 0) {
      setLoadingProgress(100); // Complete the progress when done
    }
    
    return () => {
      clearInterval(timer);
    };
  }, [loading, loadingProgress]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.custom_prompt.trim()) {
      setError('Custom Instructions are required');
      return;
    }
    
    if (!formData.project_id) {
      setError('Please select a project');
      return;
    }
    
    setLoading(true);
    setError('');
    setLoadingProgress(10);
    
    // Add a more detailed loading state message
    const loadingMsg = useEnhancedGeneration && sowAvailable
      ? "Creating your enhanced SAP functional specification using project document analysis. This process may take 3-5 minutes due to the comprehensive document analysis..."
      : "Creating your SAP functional specification. This process may take 3-5 minutes due to the complexity of the document...";
    setLoadingMessage(loadingMsg);
    
    try {
      console.log("Generating specification with:", formData);
      console.log("Using enhanced generation:", useEnhancedGeneration);
      
      // Generate the AI content with project ID included
      const aiResponse = useEnhancedGeneration 
        ? await generateSpecificationEnhanced({
            ...formData,
            // Ensure project_id is included if available
            project_id: formData.project_id || null
          })
        : await generateSpecification({
            ...formData,
            // Ensure project_id is included if available
            project_id: formData.project_id || null
          }, false);
      
      if (!aiResponse.success) {
        console.error('Generation failed with error:', aiResponse.error);
        setError(aiResponse.error || 'Failed to generate document. Please try again.');
        setLoading(false);
        return;
      }
      
      // Document now includes standard sections from backend
      let enhancedDocument = aiResponse.document;
      
      // Store the form data and generated content in session storage
      sessionStorage.setItem('originalAIContent', aiResponse.document);
      sessionStorage.setItem('generatedDocument', enhancedDocument);
      sessionStorage.setItem('documentFormat', 'md'); // Default format is markdown
      sessionStorage.setItem('formData', JSON.stringify(formData));
      sessionStorage.setItem('documentSaved', 'false'); // Initially document is not saved
      
      // If we have a project_id, store it for document association
      if (formData.project_id) {
        sessionStorage.setItem('projectId', formData.project_id);
      }
      
      // Navigate to the view document page
      navigate('/view-document');
    } catch (err) {
      console.error('Error generating document:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMessage(''); // Reset the loading message
      setLoadingProgress(0);
    }
  };
  
  const handleStepChange = (step) => {
    setActiveStep(step);
  };
  
  // Sample prompts to help users
  const samplePrompts = [
    "Create a functional specification for an SAP S/4HANA implementation project focusing on Finance and Controlling modules. Include detailed requirements for General Ledger and Cost Center Accounting.",
    "Design a functional specification for SAP Fiori implementation with custom dashboards for Sales Analytics. Include user personas, KPIs, and detailed UI requirements.",
    "Develop a functional spec for an SAP ECC enhancement project updating the Material Master process. Include custom fields, validation rules, and integration points."
  ];
  
  return (
    <Box
      sx={{ 
        minHeight: 'calc(100vh - 64px)',
        py: 6,
        background: 'linear-gradient(135deg, #121212 0%, #1e1e1e 100%)',
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundImage: 'radial-gradient(circle at 80% 10%, rgba(245, 130, 31, 0.03) 0%, rgba(0, 0, 0, 0) 50%), radial-gradient(circle at 20% 80%, rgba(29, 43, 63, 0.05) 0%, rgba(0, 0, 0, 0) 50%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`, 
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Create Your SAP Functional Specification
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary"
            sx={{ maxWidth: 700, mx: 'auto' }}
          >
            Enter your project details below to generate a comprehensive and tailored functional specification document powered by AI.
          </Typography>
          
          {/* Display selected project information */}
          {selectedProject && (
            <Box 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                px: 2, 
                py: 1, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <BusinessIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
              <Typography 
                variant="subtitle2" 
                color="primary"
                fontWeight="500"
              >
                Project: {selectedProject.name}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>

            <Card 
              elevation={3} 
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #252525 0%, #1e1e1e 100%)',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TipsAndUpdatesIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
                  <Typography variant="h6" fontWeight="medium">
                    Prompt Examples
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  Not sure what to write? Try one of these examples:
                </Typography>
                
                <Stack spacing={2}>
                  {samplePrompts.map((prompt, index) => (
                    <Card 
                      key={index} 
                      variant="outlined" 
                      sx={{ 
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderColor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          cursor: 'pointer'
                        }
                      }}
                      onClick={() => setFormData(prev => ({ ...prev, custom_prompt: prompt }))}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="body2">
                          {prompt.length > 120 ? prompt.substring(0, 120) + "..." : prompt}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1e1e1e 0%, #242424 100%)',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
                boxShadow: `0 8px 32px ${alpha('#000', 0.2)}`
              }}
            >
              {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
              
              <form onSubmit={handleSubmit}>
                <Stepper 
                  activeStep={activeStep} 
                  orientation="vertical" 
                  sx={{ 
                    mb: 3,
                    '& .MuiStepLabel-label': {
                      color: 'text.secondary'
                    },
                    '& .MuiStepLabel-label.Mui-active': {
                      color: 'text.primary'
                    },
                    '& .MuiStepConnector-line': {
                      borderColor: alpha(theme.palette.primary.main, 0.2)
                    }
                  }}
                >
                  <Step>
                    <StepLabel 
                      StepIconProps={{ 
                        sx: { 
                          color: activeStep >= 0 ? theme.palette.primary.main : undefined 
                        } 
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="500">
                        Project Information
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                          {urlProjectId && selectedProject?.project_type ? (
                            <TextField
                              label="Project Type"
                              value={selectedProject.project_type}
                              fullWidth
                              InputProps={{ readOnly: true }}
                              helperText="Auto-populated from project overview"
                            />
                          ) : (
                            <FormControl fullWidth required>
                              <InputLabel>Project Type</InputLabel>
                              <Select
                                name="project_type"
                                value={formData.project_type}
                                label="Project Type"
                                onChange={handleChange}
                              >
                                <MenuItem value="Implementation / Development Project">Implementation / Development Project</MenuItem>
                                <MenuItem value="Support and Maintenance Project">Support and Maintenance Project</MenuItem>
                                <MenuItem value="Roll Out Project">Roll Out Project</MenuItem>
                                <MenuItem value="Upgradation Project / Migration Project">Upgradation Project / Migration Project</MenuItem>
                                <MenuItem value="Enhancement project">Enhancement project</MenuItem>
                                <MenuItem value="SAP S/4HANA Cloud, Private Cloud Implementation">SAP S/4HANA Cloud, Private Cloud Implementation</MenuItem>
                                <MenuItem value="SAP S/4HANA Implementation">SAP S/4HANA Implementation</MenuItem>
                                <MenuItem value="SAP S/4HANA Migration">SAP S/4HANA Migration</MenuItem>
                                <MenuItem value="SAP ECC Enhancement">SAP ECC Enhancement</MenuItem>
                                <MenuItem value="SAP Fiori Implementation">SAP Fiori Implementation</MenuItem>
                                <MenuItem value="SAP BW/4HANA">SAP BW/4HANA</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                        
                        {/* Project - auto-populated from URL or selectable */}
                        <Grid item xs={12} md={6}>
                          {urlProjectId ? (
                            <TextField
                              label="Project"
                              value={selectedProject?.name || (projectsLoading ? 'Loading...' : 'Loading project...')}
                              fullWidth
                              InputProps={{
                                readOnly: true,
                                startAdornment: <BusinessIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                              }}
                              helperText="Auto-populated from current project"
                            />
                          ) : (
                            <FormControl fullWidth required>
                              <InputLabel>Select Project</InputLabel>
                              <Select
                                name="project_id"
                                value={formData.project_id}
                                label="Select Project"
                                onChange={handleChange}
                                disabled={projectsLoading || projects.length === 0}
                                startAdornment={
                                  <BusinessIcon sx={{ mr: 1, color: formData.project_id ? theme.palette.primary.main : 'inherit' }} />
                                }
                              >
                                {projects.length === 0 ? (
                                  <MenuItem value="" disabled>No projects available</MenuItem>
                                ) : (
                                  projects.map(project => (
                                    <MenuItem key={project.id} value={project.id.toString()}>
                                      {project.name}
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                              {projectsLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                  <CircularProgress size={16} sx={{ mr: 1 }} />
                                  <Typography variant="caption" color="text.secondary">Loading projects...</Typography>
                                </Box>
                              )}
                              {projects.length === 0 && !projectsLoading && (
                                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                  No projects are assigned to you. Contact an administrator.
                                </Typography>
                              )}
                            </FormControl>
                          )}
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Project Name"
                            name="project_name"
                            value={formData.project_name}
                            onChange={handleChange}
                            fullWidth
                            required
                            placeholder="Enter project name"
                            disabled={formData.project_id !== ''} // Disable if project selected
                            helperText={formData.project_id !== '' ? "Using selected project name" : ""}
                          />
                        </Grid>
                        
                        {/* Added Client Name field */}
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Client Name"
                            name="client_name"
                            value={formData.client_name}
                            onChange={handleChange}
                            fullWidth
                            required
                            placeholder="Enter client name"
                            helperText="Default is project name, but can be changed"
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={() => handleStepChange(1)}
                          disabled={!formData.project_type || !formData.project_name || !formData.client_name || !formData.project_id}
                          sx={{ 
                            fontWeight: 500,
                            '&.Mui-disabled': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.3),
                              color: alpha('#fff', 0.5)
                            }
                          }}
                        >
                          Continue
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                  
                  <Step>
                    <StepLabel 
                      StepIconProps={{ 
                        sx: { 
                          color: activeStep >= 1 ? theme.palette.primary.main : undefined 
                        } 
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="500">
                        Author Details
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Author Name"
                            name="author_name"
                            value={formData.author_name}
                            onChange={handleChange}
                            fullWidth
                            required
                            placeholder="Enter your full name"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Date"
                            name="date"
                            type="date"
                            value={formData.date}
                            onChange={handleChange}
                            fullWidth
                            required
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button 
                          variant="outlined"
                          color="primary"
                          onClick={() => handleStepChange(0)}
                        >
                          Back
                        </Button>
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={() => handleStepChange(2)}
                          disabled={!formData.author_name}
                          sx={{ 
                            fontWeight: 500,
                            '&.Mui-disabled': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.3),
                              color: alpha('#fff', 0.5)
                            }
                          }}
                        >
                          Continue
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                  
                  <Step>
                    <StepLabel 
                      StepIconProps={{ 
                        sx: { 
                          color: activeStep >= 2 ? theme.palette.primary.main : undefined 
                        } 
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="500">
                        Document Requirements
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        {/* Show project documents information */}
                        {selectedProject && (
                          <Grid item xs={12}>
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                mb: 2,
                                backgroundColor: sowAvailable 
                                  ? alpha(theme.palette.success.main, 0.1) 
                                  : alpha(theme.palette.warning.main, 0.1),
                                borderColor: sowAvailable 
                                  ? theme.palette.success.main 
                                  : theme.palette.warning.main
                              }}
                            >
                              <CardContent sx={{ py: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <DescriptionIcon sx={{ 
                                    color: sowAvailable ? theme.palette.success.main : theme.palette.warning.main, 
                                    mr: 1 
                                  }} />
                                  <Typography variant="subtitle2" fontWeight="600">
                                    Project Documents Status
                                  </Typography>
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary">
                                  {projectDocuments.length > 0 
                                    ? `${projectDocuments.length} document(s) found in project`
                                    : 'No documents uploaded to this project yet'
                                  }
                                </Typography>
                                
                                {sowAvailable && (
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                    <CheckCircleIcon sx={{ color: theme.palette.success.main, mr: 1, fontSize: 18 }} />
                                    <Typography variant="body2" color="success.main" fontWeight="500">
                                      Project documents detected - Enhanced AI generation available
                                    </Typography>
                                  </Box>
                                )}
                                
                                {projectDocuments.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Available documents: {projectDocuments.map(doc => doc.title).join(', ')}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                            
                            {/* Generation type selection */}
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                mb: 2,
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                borderColor: alpha(theme.palette.primary.main, 0.2)
                              }}
                            >
                              <CardContent sx={{ py: 2 }}>
                                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2 }}>
                                  AI Generation Mode
                                </Typography>
                                
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <Button
                                    variant={useEnhancedGeneration ? "contained" : "outlined"}
                                    color="primary"
                                    onClick={() => setUseEnhancedGeneration(true)}
                                    startIcon={<AutoAwesomeIcon />}
                                    disabled={!sowAvailable}
                                    sx={{ flex: 1 }}
                                  >
                                    Enhanced (Project Documents)
                                  </Button>
                                  
                                  <Button
                                    variant={!useEnhancedGeneration ? "contained" : "outlined"}
                                    color="primary"
                                    onClick={() => setUseEnhancedGeneration(false)}
                                    startIcon={<DescriptionIcon />}
                                    sx={{ flex: 1 }}
                                  >
                                    Standard
                                  </Button>
                                </Box>
                                
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  {useEnhancedGeneration 
                                    ? sowAvailable 
                                      ? "Uses project documents and project context for comprehensive requirements extraction"
                                      : "Enhanced mode requires project documents"
                                    : "Uses only custom instructions and project details"
                                  }
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        )}
                        
                        <Grid item xs={12}>
                          <TextField
                            label="Custom Instructions"
                            name="custom_prompt"
                            value={formData.custom_prompt}
                            onChange={handleChange}
                            multiline
                            rows={8}
                            fullWidth
                            required
                            placeholder={
                              useEnhancedGeneration && sowAvailable
                                ? "Provide specific instructions to supplement the project document analysis (e.g., technical preferences, specific SAP modules to focus on, custom requirements not in the project documents)"
                                : "Provide detailed instructions about what to include in your SAP functional specification"
                            }
                            helperText={
                              <Box>
                                {useEnhancedGeneration && sowAvailable 
                                  ? "Instructions will complement the automatic project document analysis"
                                  : "Be as specific as possible to get the best results across all sections"
                                }
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                  <BusinessIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mr: 1 }} />
                                  <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                    Project: {selectedProject?.name || 'No project selected'}
                                  </Typography>
                                  {sowAvailable && (
                                    <>
                                      <Box sx={{ mx: 1, color: theme.palette.primary.main }}>•</Box>
                                      <AutoAwesomeIcon sx={{ fontSize: 16, color: theme.palette.success.main, mr: 0.5 }} />
                                      <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                                        Project Documents Enhanced
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              </Box>
                            }
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: alpha('#fff', 0.02)
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button 
                          variant="outlined"
                          color="primary"
                          onClick={() => handleStepChange(1)}
                        >
                          Back
                        </Button>
                        <Button 
                          type="submit" 
                          variant="contained" 
                          color="primary"
                          disabled={loading || !formData.custom_prompt.trim() || !formData.project_id}
                          startIcon={<RocketLaunchIcon />}
                          sx={{ 
                            fontWeight: 500,
                            px: 3,
                            py: 1.2,
                            '&.Mui-disabled': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.3),
                              color: alpha('#fff', 0.5)
                            }
                          }}
                        >
                          {loading ? 'Generating...' : 'Generate Specification'}
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                </Stepper>
                
                {loading && (
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      mt: 4, 
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AutoAwesomeIcon 
                          sx={{ 
                            color: theme.palette.primary.main, 
                            mr: 1.5,
                            animation: 'pulse 2s infinite'
                          }} 
                        />
                        <Typography variant="h6" color="primary" fontWeight="medium">
                          Creating Your Document
                        </Typography>
                      </Box>
                      
                      <LinearProgress 
                        variant="determinate" 
                        value={loadingProgress} 
                        sx={{ 
                          my: 2, 
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: theme.palette.primary.main
                          }
                        }} 
                      />
                      
                      <Typography variant="body2" color="text.secondary">
                        {loadingMessage}
                      </Typography>
                      
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'block', 
                          mt: 1, 
                          textAlign: 'center' 
                        }}
                      >
                        {loadingProgress < 30 ? 'Analyzing requirements...' : 
                         loadingProgress < 60 ? 'Building document structure...' : 
                         loadingProgress < 90 ? 'Generating detailed content...' : 
                         'Finalizing document...'}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GenerateSpecPage;
